import type { CSVParseConfig } from 'components/DataTransferForm/types';
import type { DeviceAttributesRowData } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/DeviceAttributesRowData';
import { generateGuid } from 'utils/guid';

// CSV headers for device attributes export/import
export const deviceAttributesFileHeaders = [
  'Serial number',
  'Hostname',
  'Attribute',
  'Value',
] as const;

// Maximum file size for CSV import: 10MB
export const MAX_CSV_FILE_SIZE = 10 * 1024 * 1024;

const checkExistingDataDuplicates = (
  serialNumber: string,
  attributeName: string,
  existingData: DeviceAttributesRowData[],
  isReplaceMode: boolean,
): string | null => {
  if (isReplaceMode) {
    return null;
  }

  const existingDuplicate = existingData.find((existing) => {
    const existingSn = existing.sn?.trim() || '';
    const existingName = existing.name?.trim() || '';

    return existingSn === serialNumber && existingName === attributeName;
  });

  if (existingDuplicate) {
    return `Duplicate found: Serial number "${serialNumber}" already has attribute "${attributeName}" in the table.`;
  }

  return null;
};

export const createDeviceAttributesCsvConfig = (
  existingData: DeviceAttributesRowData[] = [],
  isReplaceMode = false,
): CSVParseConfig<DeviceAttributesRowData> => {
  const csvRowTracker = new Set<string>();

  return {
    headers: deviceAttributesFileHeaders,
    maxFileSizeBytes: MAX_CSV_FILE_SIZE,
    rules: [
      {
        field: 'Serial number',
        required: true,
        validator: (value) => {
          if (!value.trim()) {
            return 'Serial number is required';
          }
          return null;
        },
      },
      {
        field: 'Hostname',
        required: false,
      },
      {
        field: 'Attribute',
        required: true,
        validator: (value, row) => {
          if (!row?.Attribute?.trim()) {
            return 'Attribute is required';
          }

          const serialNumber = row['Serial number']?.trim();
          const attributeName = value.trim();

          const duplicateKey = `${serialNumber}::${attributeName}`;

          if (csvRowTracker.has(duplicateKey)) {
            return `Duplicate in CSV: Serial number "${serialNumber}" with attribute "${attributeName}" appears multiple times in the file.`;
          }

          const existingDuplicateError = checkExistingDataDuplicates(
            serialNumber || '',
            attributeName,
            existingData,
            isReplaceMode,
          );

          if (existingDuplicateError) {
            return existingDuplicateError;
          }

          csvRowTracker.add(duplicateKey);

          return null;
        },
      },
      {
        field: 'Value',
        required: true,
        validator: (value) => {
          if (!value.trim()) {
            return 'Value is required';
          }
          return null;
        },
      },
    ],
    rowTransformer: (validatedRow): DeviceAttributesRowData => {
      const serialNumber = validatedRow['Serial number'];
      const hostname = validatedRow.Hostname;
      const attributeName = validatedRow.Attribute;
      const attributeValue = validatedRow.Value;

      const sn = typeof serialNumber === 'string' ? serialNumber : '';
      const name = typeof attributeName === 'string' ? attributeName : '';
      const value = typeof attributeValue === 'string' ? attributeValue : '';

      return {
        id: generateGuid(),
        sn,
        hostname: typeof hostname === 'string' ? hostname : '',
        name,
        value,
      };
    },
  };
};
